import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { DATE_FORMAT_ISO_DASH_SMALL_CASE_YEAR, DATE_FORMAT_YEAR_MONTH } from '@/constant';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePickerFieldType } from '@/type';
import { hu } from 'date-fns/locale';
import { buildFormFieldLabel } from '@/util';
import { StringSchema } from 'yup';
import InputAdornment from '@mui/material/InputAdornment';

interface DatePickerInputProps {
  field?: any;
  dateField?: DatePickerFieldType;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const DatePickerInput = ({
  field,
  dateField,
  error,
  helperText,
  disabled,
}: DatePickerInputProps) => {
  const { t } = useTranslation();

  const handleDateChange = (date: Date | null) => {
    if (date instanceof Date && !isNaN(date.getTime())) {
      const formattedDate =
        dateField?.viewMode === 'year-month'
          ? format(date, DATE_FORMAT_YEAR_MONTH)
          : format(date, DATE_FORMAT_ISO_DASH_SMALL_CASE_YEAR);
      field.onChange(formattedDate);
    } else {
      field.onChange('');
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={hu}
      localeText={{ todayButtonLabel: t('common.form.now') }}
    >
      <DatePicker<Date>
        id={field.name}
        {...field}
        className='date-input'
        value={field.value ? parseISO(field.value) : null}
        label={buildFormFieldLabel(
          t(dateField.label),
          dateField.validation as StringSchema,
        )}
        disabled={disabled || dateField.disabled}
        defaultValue={dateField.defaultValue}
        minDate={dateField.inputProps?.min}
        maxDate={dateField.inputProps?.max}
        onChange={handleDateChange}
        sx={{ width: '100%' }}
        views={dateField.viewMode === 'year-month' ? ['year', 'month'] : undefined}
        slotProps={{
          textField: {
            error: !!error,
            helperText,
            InputLabelProps: {
              shrink: true,
            },
            InputProps: {
              startAdornment: dateField.preFix ? (
                <InputAdornment position='start'>{dateField.preFix}</InputAdornment>
              ) : null,
            },
            inputProps: {
              placeholder: '',
            },
          },
          openPickerButton: {
            className: 'custom-calendar-button',
          },
          actionBar: {
            actions: ['today'],
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default DatePickerInput;
